import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useQuery } from '@apollo/react-hooks';

import Tabs from '@material-ui/core/Tabs';
import Button from '@material-ui/core/Button';
import { Tab, Typography } from '@material-ui/core';
import DateRangeIcon from '@material-ui/icons/DateRange';

import { AppHeader, TabPanel, SelectCompanyBox } from 'components';
import UpcomingAccommodations from './components/upcoming-accommodations/UpcomingAccommodations';
import UpcomingActivities from './components/upcoming-activities/UpcomingActivities';
import { OVERVIEW } from 'graphql/queries/overview';

import useStyles from './styles';
import SelectDropDown from '../../components/select-drop-down/SelectDropDown';
import EntityTab from './components/ToolbarTabs';
import ToolbarTabs from './components/ToolbarTabs';

const CURRENT_TAB = 0;
const CANCELED_TAB = 1;
const SORTED_BY_ACCOMMODATIONS = 'accommodationsBookings';
const SORTED_BY_ACTIVITIES = 'activitiesBookings';
const ACCOMMODATIONS_OVERVIEW_INDEX = 0;
const ACTIVITIES_OVERVIEW_INDEX = 1;

const Overview = ({ location, businessOwner }) => {
  const classes = useStyles();
  const history = useHistory();
  const [t] = useTranslation();

  const [tab, setTab] = useState(location.state?.tabNumber || 0);
  const [upcomingPeriod, setUpcomingPeriod] = useState('today');
  const [
    upcomingPeriodForActivities,
    setUpcomingPeriodForActivities
  ] = useState('today');

  const overviewType =
    tab === ACCOMMODATIONS_OVERVIEW_INDEX
      ? 'accommodationsBookingsOverview'
      : 'activitiesBookingsOverview';

  const { data, refetch } = useQuery(OVERVIEW, {
    variables: {
      businessOwnerId:
        businessOwner.selectedBusinessOwner.ownerId ||
        businessOwner.selectedBusinessOwner.id
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const handleChangeTab = (event, newTab) => {
    setTab(newTab);
    refetch();
  };

  const handleChangeUpcomingPeriod = newValue => {
    setUpcomingPeriod(newValue);
  };

  const handleChangeUpcomingPeriodForActivities = newValue => {
    setUpcomingPeriodForActivities(newValue);
  };

  return (
    <div className={classes.root}>
      <AppHeader />
      {!businessOwner.selectedBusinessOwner.id && <SelectCompanyBox />}

      {businessOwner.selectedBusinessOwner.id && (
        <div className={classes.toolbar}>
          <div className={classes.toolbarLeft}>
            <Typography variant="h2" className={classes.toolbarTitle}>
              {t('Overview')}
            </Typography>
          </div>

          <ToolbarTabs tab={tab} onChangeTab={handleChangeTab} />
        </div>
      )}

      {businessOwner.selectedBusinessOwner.id && data && (
        <div className={classes.content}>
          <div className={classes.horizontalTabs}>
            <div
              className={
                tab === ACCOMMODATIONS_OVERVIEW_INDEX
                  ? classes.accommodationOverviewTab
                  : classes.activityOverviewTab
              }
            >
              <Typography className={classes.numberTitle}>
                {data[overviewType].newBookingsNumber}
              </Typography>
              <Typography className={classes.tabDescription}>
                {t('New bookings today')}
              </Typography>
              <Typography
                className={classes.seeMoreOption}
                onClick={() => {
                  history.push({
                    pathname: '/bookings-list',
                    state: {
                      tabNumber: CURRENT_TAB,
                      sortedBy:
                        tab === ACCOMMODATIONS_OVERVIEW_INDEX
                          ? SORTED_BY_ACCOMMODATIONS
                          : SORTED_BY_ACTIVITIES
                    }
                  });
                }}
              >
                {t('See more')}
              </Typography>
            </div>
            <div
              className={
                tab === ACCOMMODATIONS_OVERVIEW_INDEX
                  ? classes.accommodationOverviewTab
                  : classes.activityOverviewTab
              }
            >
              <Typography className={classes.numberTitle}>
                {data[overviewType].currentBookingsNumber}
              </Typography>
              <Typography className={classes.tabDescription}>
                {t('Active bookings')}
              </Typography>
              <Typography
                className={classes.seeMoreOption}
                onClick={() => {
                  history.push({
                    pathname: '/bookings-list',
                    state: {
                      tabNumber: CURRENT_TAB,
                      sortedBy:
                        tab === ACCOMMODATIONS_OVERVIEW_INDEX
                          ? SORTED_BY_ACCOMMODATIONS
                          : SORTED_BY_ACTIVITIES
                    }
                  });
                }}
              >
                {t('See more')}
              </Typography>
            </div>
            <div
              className={
                tab === ACCOMMODATIONS_OVERVIEW_INDEX
                  ? classes.accommodationOverviewTab
                  : classes.activityOverviewTab
              }
            >
              <Typography className={classes.numberTitle}>
                {data[overviewType].bookingsWhichStartToday
                  ? data[overviewType].bookingsWhichStartToday
                  : 0}
              </Typography>
              <Typography className={classes.tabDescription}>
                {t('Bookings with check-in today')}
              </Typography>
              <Typography className={classes.numberTitle}>
                {data[overviewType].arrivalGuestsNumber
                  ? data[overviewType].arrivalGuestsNumber
                  : 0}
              </Typography>
              <Typography className={classes.tabDescription}>
                {t('Guests scheduled for arrival today')}
              </Typography>
            </div>
            {tab === ACCOMMODATIONS_OVERVIEW_INDEX && (
              <div className={classes.accommodationOverviewTab}>
                <Typography className={classes.numberTitle}>
                  {data[overviewType].bookingsWhichEndToday
                    ? data[overviewType].bookingsWhichEndToday
                    : 0}
                </Typography>
                <Typography className={classes.tabDescription}>
                  {t('Bookings with check-out today')}
                </Typography>
                <Typography className={classes.numberTitle}>
                  {data[overviewType].departureGuestsNumber
                    ? data[overviewType].departureGuestsNumber
                    : 0}
                </Typography>
                <Typography className={classes.tabDescription}>
                  {t('Guests scheduled for departure today')}
                </Typography>
              </div>
            )}
            <div
              className={
                tab === ACCOMMODATIONS_OVERVIEW_INDEX
                  ? classes.accommodationOverviewTab
                  : classes.activityOverviewTab
              }
            >
              <Typography className={classes.numberTitle}>
                {data[overviewType].canceledBookingsNumber}
              </Typography>

              <Typography className={classes.tabDescription}>
                {t('Bookings are cancelled')}
              </Typography>

              <Typography
                className={classes.seeMoreOption}
                onClick={() => {
                  history.push({
                    pathname: '/bookings-list',
                    state: {
                      tabNumber: CANCELED_TAB,
                      sortedBy: 'allBookings'
                    }
                  });
                }}
              >
                {t('See more')}
              </Typography>
            </div>
          </div>

          <TabPanel
            className={classes.tabContentWrapper}
            value={tab}
            index={ACCOMMODATIONS_OVERVIEW_INDEX}
          >
            <div>
              <div className={classes.viewSwitchers}>
                <Typography className={classes.tableTitle}>
                  {t('Upcoming accommodations')}
                </Typography>
                <SelectDropDown
                  value={upcomingPeriod}
                  onChange={handleChangeUpcomingPeriod}
                  className={classes.selectInput}
                  items={[
                    {
                      value: 'today',
                      label: 'today'
                    },
                    {
                      value: 'week',
                      label: '7 days'
                    },
                    {
                      value: 'month',
                      label: '30 days'
                    }
                  ]}
                />
                <Button
                  classes={{ root: classes.button }}
                  onClick={() =>
                    history.push(`/accommodations-overview-calendar`)
                  }
                >
                  <DateRangeIcon
                    fontSize="small"
                    className={classes.calendarIcon}
                  />
                  <Typography className={classes.calendarTitle}>
                    {t('Show Calendar')}
                  </Typography>
                </Button>
              </div>
              <UpcomingAccommodations
                ownerId={
                  businessOwner.selectedBusinessOwner.ownerId ||
                  businessOwner.selectedBusinessOwner.id
                }
                upcomingPeriod={upcomingPeriod}
              />
            </div>
          </TabPanel>
          <TabPanel
            className={classes.tabContentWrapper}
            value={tab}
            index={ACTIVITIES_OVERVIEW_INDEX}
          >
            <div>
              <div className={classes.viewSwitchers}>
                <Typography className={classes.tableTitle}>
                  {t('Upcoming activities')}
                </Typography>
                <SelectDropDown
                  value={upcomingPeriodForActivities}
                  onChange={handleChangeUpcomingPeriodForActivities}
                  className={classes.selectInput}
                  items={[
                    {
                      value: 'today',
                      label: 'today'
                    },
                    {
                      value: 'week',
                      label: '7 days'
                    },
                    {
                      value: 'month',
                      label: '30 days'
                    }
                  ]}
                />
                <Button
                  classes={{ root: classes.button }}
                  onClick={() => history.push(`/activities-overview-calendar`)}
                >
                  <DateRangeIcon
                    fontSize="small"
                    className={classes.calendarIcon}
                  />
                  <Typography className={classes.calendarTitle}>
                    {t('Show Calendar')}
                  </Typography>
                </Button>
              </div>
              <UpcomingActivities
                upcomingPeriod={upcomingPeriodForActivities}
                ownerId={
                  businessOwner.selectedBusinessOwner.ownerId ||
                  businessOwner.selectedBusinessOwner.id
                }
              />
            </div>
          </TabPanel>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  businessOwner: state.businessOwner
});

export default connect(mapStateToProps, null)(Overview);
